import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { TbPictureInPictureOff } from 'react-icons/tb';
import { ChatAIAvatar } from '../ChatAIAvatar/ChatAIAvatar';

import { STATUS_MINI_TEXTS } from '../constants';
import { ChatAIIndicatorStatus } from '@/hooks/useChatAIStatus';
import './ChatAIMiniature.scss';

export interface ChatAIMiniatureProps {
    assistantName: string;
    isTyping: boolean;
    completedReplyId?: string;
    status: ChatAIIndicatorStatus;
    onExpand: () => void;
    onClose: () => void;
}

const ChatAIMiniature: React.FC<ChatAIMiniatureProps> = ({
    assistantName,
    isTyping,
    completedReplyId,
    status,
    onExpand,
    onClose,
}) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const id = window.requestAnimationFrame(() => setVisible(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    const handleExpand = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        onExpand();
    };

    const handleClose = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        onClose();
    };

    const handleRootKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onExpand();
        }
    };

    const node = (
        <div
            className={`chat-ai-mini chat-ai-mini--${status}${visible ? ' is-visible' : ''}${
                isTyping ? ' is-typing' : ''
            }`}
            role="button"
            tabIndex={0}
            aria-label={`Развернуть чат с ассистентом: ${assistantName}`}
            onClick={onExpand}
            onKeyDown={handleRootKeyDown}
        >
            <span className="chat-ai-mini__aura" aria-hidden />

            <ChatAIAvatar compact working={isTyping} completedReplyId={completedReplyId} />

            <span className="chat-ai-mini__body">
                <span className="chat-ai-mini__name">{assistantName}</span>
                <span
                    className={`chat-ai-mini__status${isTyping ? ' is-typing' : ''}`}
                    aria-live="polite"
                >
                    {isTyping ? (
                        <>
                            <span className="chat-ai-mini__dots" aria-hidden>
                                <i />
                                <i />
                                <i />
                            </span>
                            <span className="chat-ai-mini__status-text">печатает</span>
                        </>
                    ) : (
                        <>
                            <span
                                className={`chat-ai-mini__status-dot chat-ai-mini__status-dot--${status}`}
                                aria-hidden
                            />
                            <span className="chat-ai-mini__status-text">
                                {STATUS_MINI_TEXTS[status]}
                            </span>
                        </>
                    )}
                </span>
            </span>

            <div className="chat-ai-mini__overlay">
                <span className="chat-ai-mini__overlay-ink" aria-hidden />
                <button
                    type="button"
                    className="chat-ai-mini__expand"
                    aria-label="Развернуть"
                    onClick={handleExpand}
                >
                    <TbPictureInPictureOff size={20} aria-hidden />
                    <span className="chat-ai-mini__expand-text">Развернуть</span>
                </button>
                <button
                    type="button"
                    className="chat-ai-mini__close"
                    aria-label="Закрыть чат"
                    onClick={handleClose}
                >
                    <MdClose size={16} aria-hidden />
                </button>
            </div>
        </div>
    );

    return createPortal(node, document.body);
};

export default ChatAIMiniature;
